import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Search, X, Download, GraduationCap, Users, MapPin, FileCheck, ShieldCheck, Monitor, Sparkles, ArrowRight, CalendarDays, Euro } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { TeamBand } from "@/components/TeamBand";
import { VideoPresentation } from "@/components/VideoPresentation";
import { Stars } from "@/components/Stars";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["Librement commercialisable", "Réglementée", "Habilitation requise"];
const badgeStyle = (s) =>
  s === "Réglementée"
    ? { background: "var(--orange)", color: "var(--bone)" }
    : s === "Habilitation requise"
    ? { background: "var(--ink)", color: "var(--bone)" }
    : { background: "transparent", color: "var(--ink-2)", border: "1px solid var(--border)" };

export default function Formations() {
  const [formations, setFormations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [domain, setDomain] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessForm, setSessForm] = useState({ name: "", email: "", phone: "", company: "", participants: 1 });
  const [sessLoading, setSessLoading] = useState(false);

  const submitSession = async (e) => {
    e.preventDefault();
    setSessLoading(true);
    try {
      const { data } = await api.post(`/sessions/${activeSession.id}/register`, { ...sessForm, participants: Number(sessForm.participants) });
      toast.success(data.message);
      setSessions((prev) => prev.map((s) => (s.id === activeSession.id ? { ...s, seats_left: data.seats_left } : s)));
      setActiveSession(null);
      setSessForm({ name: "", email: "", phone: "", company: "", participants: 1 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSessLoading(false);
    }
  };

  useEffect(() => {
    api.get("/formations").then((r) => setFormations(r.data)).catch(() => {});
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const domains = [...new Set(formations.map((f) => f.domain).filter(Boolean))];
  let shown = domain === "all" ? formations : formations.filter((f) => f.domain === domain);
  if (status !== "all") shown = shown.filter((f) => f.status === status);
  if (q.trim()) {
    const t = q.toLowerCase();
    shown = shown.filter((f) => f.title.toLowerCase().includes(t) || (f.domain || "").toLowerCase().includes(t));
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/registrations", { ...form, formation_id: active.id, formation_title: active.title });
      toast.success("Inscription enregistrée ! Nous vous recontactons rapidement.");
      setActive(null);
      setForm({ name: "", email: "", phone: "", company: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="formations-page" className="pt-[148px]">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Catalogue de formations</p></Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none">Des formations qui font<br />grandir vos équipes.</h1>
          <p className="mt-6 text-lg max-w-3xl" style={{ color: "var(--ink-2)" }}>
            {formations.length} formations concrètes et immédiatement applicables : sécurité au travail, qualité, environnement, ergonomie, mais aussi <strong style={{ color: "var(--ink)" }}>bureautique (Word, Excel, PowerPoint, Access)</strong>, <strong style={{ color: "var(--ink)" }}>développement personnel</strong>, <strong style={{ color: "var(--ink)" }}>rédaction administrative</strong> et valorisation des compétences. Animées par des formateurs agréés, en présentiel dans vos locaux ou à distance, elles transforment chaque session en résultats visibles sur le terrain.
          </p>
        </Reveal>

        {/* POURQUOI SE FORMER */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }} data-testid="formations-benefits">
          {[
            { icon: GraduationCap, t: "Formateurs agréés", d: "Des experts certifiés INRS et praticiens de terrain, pas des lecteurs de diapositives." },
            { icon: MapPin, t: "Intra ou distanciel", d: "Dans vos locaux partout en France, ou à distance : c'est vous qui choisissez." },
            { icon: Users, t: "Sur-mesure", d: "Programmes adaptés à vos métiers, vos risques et le niveau réel de vos équipes." },
            { icon: FileCheck, t: "Attestation remise", d: "Attestation de fin de formation et supports pédagogiques inclus pour chaque stagiaire." },
          ].map((b, i) => (
            <Reveal key={b.t} delay={i * 0.06}>
              <div className="p-6 h-full" style={{ background: "var(--white)" }} data-testid={`formation-benefit-${i}`}>
                <b.icon size={24} style={{ color: "var(--orange)" }} />
                <h3 className="font-display font-bold mt-4 mb-2">{b.t}</h3>
                <p className="text-sm" style={{ color: "var(--ink-2)" }}>{b.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* 3 UNIVERS */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }} data-testid="formations-univers">
          {[
            { icon: ShieldCheck, t: "Prévention & sécurité", d: "SST, incendie, PRAP, habilitations, CSE… Protégez vos équipes et restez conforme à vos obligations.", filter: "all" },
            { icon: Monitor, t: "Bureautique & numérique", d: "Word, Excel, PowerPoint, Access : des compétences immédiatement productives, du débutant à l'expert.", filter: "Bureautique" },
            { icon: Sparkles, t: "Développement personnel & efficacité", d: "Gestion du temps, prise de parole, rédaction administrative, valorisation des compétences : révélez les talents.", filter: "Développement personnel" },
          ].map((u, i) => (
            <Reveal key={u.t} delay={i * 0.07}>
              <button onClick={() => { setDomain(u.filter); setStatus("all"); }} className="group p-8 h-full w-full text-left transition-colors duration-300"
                style={{ background: "var(--ink)", color: "var(--bone)" }} data-testid={`univers-${i}`}>
                <u.icon size={28} style={{ color: "var(--orange)" }} />
                <h3 className="font-display font-black text-2xl mt-5 mb-3">{u.t}</h3>
                <p className="text-sm" style={{ color: "rgba(245,245,240,0.75)" }}>{u.d}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold" style={{ color: "var(--orange)" }}>
                  Explorer <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        {/* FINANCEMENT OPCO */}
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 p-6" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="opco-banner">
            <div className="flex items-center justify-center w-12 h-12 shrink-0 rounded-full" style={{ background: "var(--orange)" }}>
              <Euro size={22} style={{ color: "var(--bone)" }} />
            </div>
            <p style={{ color: "var(--ink-2)" }}>
              <strong style={{ color: "var(--ink)" }}>Toutes nos formations sont finançables par votre OPCO.</strong>{" "}
              Nous vous fournissons programme détaillé, convention et devis conformes, et nous vous aidons à monter votre dossier de prise en charge. <Link to="/contact" className="font-semibold underline underline-offset-4" style={{ color: "var(--orange)" }}>Parlez-en à un conseiller</Link>.
            </p>
          </div>
        </Reveal>

        {/* PROCHAINES SESSIONS */}
        {sessions.length > 0 && (
          <div className="mt-14" data-testid="sessions-section">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <CalendarDays size={24} style={{ color: "var(--orange)" }} />
                <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Prochaines sessions inter-entreprises</h2>
              </div>
            </Reveal>
            <div className="overflow-x-auto" style={{ border: "1px solid var(--border)" }}>
              <table className="w-full text-sm" style={{ background: "var(--white)" }}>
                <thead>
                  <tr className="text-left" style={{ background: "var(--ink)", color: "var(--bone)" }}>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap">Date</th>
                    <th className="px-5 py-4 font-semibold">Formation</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap">Lieu</th>
                    <th className="px-5 py-4 font-semibold whitespace-nowrap">Places</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.slice(0, 10).map((s) => (
                    <tr key={s.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`session-row-${s.id}`}>
                      <td className="px-5 py-4 font-bold whitespace-nowrap" style={{ color: "var(--orange)" }}>
                        {new Date(s.date + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      </td>
                      <td className="px-5 py-4">
                        <Link to={`/formations/${s.formation_id}`} className="font-semibold link-hover">{s.formation_title}</Link>
                        {s.rating_avg > 0 && (
                          <span className="inline-flex items-center gap-1.5 ml-3 align-middle" data-testid={`session-rating-${s.id}`}>
                            <Stars value={s.rating_avg} size={13} />
                            <span className="text-xs font-bold" style={{ color: "var(--orange)" }}>{String(s.rating_avg).replace(".", ",")}/5</span>
                          </span>
                        )}
                        <span className="block text-xs mt-0.5" style={{ color: "var(--ink-2)" }}>{s.domain} · {s.duration}</span>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap" style={{ color: "var(--ink-2)" }}>{s.city}</td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1" style={s.seats_left <= 4 ? { background: "var(--orange)", color: "var(--bone)" } : { border: "1px solid var(--border)", color: "var(--ink-2)" }}>
                          {s.seats_left} restantes
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {s.seats_left > 0 ? (
                          <button onClick={() => setActiveSession(s)} className="btn-primary px-4 py-2 text-xs font-bold whitespace-nowrap" data-testid={`session-register-${s.id}`}>S'inscrire</button>
                        ) : (
                          <span className="px-4 py-2 text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ background: "var(--ink)", color: "var(--bone)", opacity: 0.6 }} data-testid={`session-full-${s.id}`}>Complet</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs" style={{ color: "var(--ink-2)" }}>Sessions garanties à partir de 4 participants. D'autres dates sont proposées en intra-entreprise, partout en France.</p>
          </div>
        )}

        <Reveal delay={0.1}>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/formations/catalogue/pdf`} target="_blank" rel="noopener noreferrer"
            className="btn-outline mt-10 inline-flex items-center gap-2 px-6 py-3 font-semibold" data-testid="download-catalogue-pdf-btn">
            <Download size={16} /> Télécharger le catalogue complet (PDF)
          </a>
        </Reveal>

        <div className="mt-10 relative max-w-md" data-testid="formations-search-wrap">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--ink-2)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une formation…" data-testid="formations-search"
            className="field" style={{ paddingLeft: "2.5rem" }} />
        </div>

        <div className="mt-5 flex flex-wrap gap-2" data-testid="domain-filters">
          <button onClick={() => setDomain("all")} data-testid="domain-all"
            className="px-4 py-2 text-xs font-semibold transition-colors duration-200"
            style={{ border: "1px solid var(--ink)", background: domain === "all" ? "var(--ink)" : "transparent", color: domain === "all" ? "var(--bone)" : "var(--ink)" }}>
            Tous les domaines ({formations.length})
          </button>
          {domains.map((d) => {
            const c = formations.filter((f) => f.domain === d).length;
            return (
              <button key={d} onClick={() => setDomain(d)} data-testid={`domain-${d}`}
                className="px-4 py-2 text-xs font-semibold transition-colors duration-200"
                style={{ border: "1px solid var(--border)", background: domain === d ? "var(--orange)" : "transparent", color: domain === d ? "var(--bone)" : "var(--ink-2)" }}>
                {d} ({c})
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filters">
          <button onClick={() => setStatus("all")} data-testid="status-all"
            className="px-4 py-2 text-xs font-semibold" style={{ border: "1px solid var(--ink)", background: status === "all" ? "var(--ink)" : "transparent", color: status === "all" ? "var(--bone)" : "var(--ink)" }}>
            Tous statuts
          </button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`status-${s}`}
              className="px-4 py-2 text-xs font-semibold" style={{ ...badgeStyle(s), opacity: status === s || status === "all" ? 1 : 0.55 }}>
              {s} ({formations.filter((f) => f.status === s).length})
            </button>
          ))}
        </div>
        <p className="text-sm mb-6" style={{ color: "var(--ink-2)" }}>{shown.length} formation{shown.length > 1 ? "s" : ""}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
          {shown.map((f) => (
            <div key={f.id} className="group flex flex-col" style={{ background: "var(--white)" }} data-testid={`formation-card-${f.id}`}>
              <Link to={`/formations/${f.id}`} className="overflow-hidden block" data-testid={`formation-link-${f.id}`}>
                <img src={f.image} alt={f.title} loading="lazy" className="w-full h-40 object-cover transition-transform duration-700 group-hover:scale-105" />
              </Link>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="overline" style={{ color: "var(--orange)" }}>{f.domain}</span>
                  <VideoPresentation endpoint={`/formations/${f.id}`} variant="icon" />
                </div>
                <Link to={`/formations/${f.id}`} className="flex-1">
                  <h3 className="font-display font-bold text-lg leading-tight link-hover inline">{f.title}</h3>
                </Link>
                <span className="mt-3 self-start text-[10px] uppercase tracking-wider font-semibold px-2 py-1" style={badgeStyle(f.status)}>{f.status}</span>
                {f.rating_count > 0 && (
                  <div className="flex items-center gap-1 mt-3" data-testid={`formation-card-rating-${f.id}`}>
                    <Stars value={f.rating_avg} size={13} />
                    <span className="text-xs" style={{ color: "var(--ink-2)" }}>({f.rating_count})</span>
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-sm" style={{ color: "var(--ink-2)" }}><Clock size={15} /> {f.duration}</span>
                  <button onClick={() => setActive(f)} data-testid={`formation-inscrire-${f.id}`} className="btn-primary px-4 py-2 text-sm font-semibold">S'inscrire</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {shown.length === 0 && <p className="py-16 text-center" style={{ color: "var(--ink-2)" }}>Aucune formation ne correspond à votre recherche.</p>}

        <div className="mt-12 p-6 md:p-8 text-sm" style={{ background: "var(--white)", border: "1px solid var(--border)", color: "var(--ink-2)" }} data-testid="formations-note">
          <strong style={{ color: "var(--ink)" }}>Bon à savoir :</strong> les durées indiquées sont commerciales et indicatives. Pour les formations SST, PRAP, CATEC, certaines habilitations électriques, échafaudages et les formations réglementées des représentants du personnel, les référentiels, durées, compétences et habilitations applicables sont respectés.
        </div>

        {/* CTA SUR-MESURE */}
        <Reveal>
          <div className="mt-12 flex flex-col md:flex-row items-center justify-between gap-6 p-10 md:p-14" style={{ background: "var(--orange)", color: "var(--bone)" }} data-testid="formations-cta">
            <div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-none">Vous ne trouvez pas la formation idéale ?</h2>
              <p className="mt-4 text-lg max-w-xl" style={{ color: "rgba(245,245,240,0.9)" }}>Nous construisons des parcours 100 % sur-mesure à partir de vos métiers et de vos objectifs. Réponse sous 48h.</p>
            </div>
            <Link to="/contact" data-testid="formations-cta-btn" className="px-10 py-5 font-display font-bold text-lg whitespace-nowrap" style={{ background: "var(--ink)", color: "var(--bone)" }}>Demander un programme</Link>
          </div>
        </Reveal>
      </section>

      <TeamBand compact />

      {active && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(15,17,21,0.6)" }} data-testid="registration-modal">
          <div className="w-full max-w-lg p-8 md:p-10 relative" style={{ background: "var(--bone)", border: "1px solid var(--ink)" }}>
            <button onClick={() => setActive(null)} className="absolute top-5 right-5" data-testid="modal-close"><X size={24} /></button>
            <p className="overline mb-2" style={{ color: "var(--orange)" }}>Inscription · {active.domain}</p>
            <h3 className="font-display font-bold text-2xl mb-1">{active.title}</h3>
            <p className="text-sm mb-6" style={{ color: "var(--ink-2)" }}>Durée indicative : {active.duration}</p>
            <form onSubmit={submit} className="space-y-4">
              <input className="field" placeholder="Nom complet *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name" />
              <input className="field" type="email" placeholder="Email *" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" />
              <input className="field" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="reg-phone" />
              <input className="field" placeholder="Entreprise" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="reg-company" />
              <button type="submit" disabled={loading} className="btn-primary w-full py-4 font-semibold" data-testid="reg-submit">
                {loading ? "Envoi..." : "Confirmer mon inscription"}
              </button>
            </form>
          </div>
        </div>
      )}
      {activeSession && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(15,17,21,0.6)" }} data-testid="session-registration-modal">
          <div className="w-full max-w-lg p-8 md:p-10 relative" style={{ background: "var(--bone)", border: "1px solid var(--ink)" }}>
            <button onClick={() => setActiveSession(null)} className="absolute top-5 right-5" data-testid="session-modal-close"><X size={24} /></button>
            <p className="overline mb-2" style={{ color: "var(--orange)" }}>
              Session du {new Date(activeSession.date + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </p>
            <h3 className="font-display font-bold text-2xl mb-1">{activeSession.formation_title}</h3>
            <p className="text-sm mb-6" style={{ color: "var(--ink-2)" }}>{activeSession.city} · {activeSession.seats_left} place(s) restante(s)</p>
            <form onSubmit={submitSession} className="space-y-4">
              <input className="field" placeholder="Nom complet *" required value={sessForm.name} onChange={(e) => setSessForm({ ...sessForm, name: e.target.value })} data-testid="sess-reg-name" />
              <input className="field" type="email" placeholder="Email *" required value={sessForm.email} onChange={(e) => setSessForm({ ...sessForm, email: e.target.value })} data-testid="sess-reg-email" />
              <input className="field" placeholder="Téléphone" value={sessForm.phone} onChange={(e) => setSessForm({ ...sessForm, phone: e.target.value })} data-testid="sess-reg-phone" />
              <input className="field" placeholder="Entreprise" value={sessForm.company} onChange={(e) => setSessForm({ ...sessForm, company: e.target.value })} data-testid="sess-reg-company" />
              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold shrink-0" htmlFor="sess-participants">Participants :</label>
                <input id="sess-participants" className="field" type="number" min="1" max={Math.max(1, Math.min(10, activeSession.seats_left))} value={sessForm.participants}
                  onChange={(e) => setSessForm({ ...sessForm, participants: e.target.value })} data-testid="sess-reg-participants" />
              </div>
              <button type="submit" disabled={sessLoading} className="btn-primary w-full py-4 font-semibold" data-testid="sess-reg-submit">
                {sessLoading ? "Envoi..." : "Confirmer mon inscription"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
